import styles from './index.module.css'

import React, { useCallback, useEffect, useMemo } from 'react'
import { Outlet, useLocation, useParams } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { ALBUMS, LICENSE_REQUEST } from '@disco/constants/FeatureTypes'
import { useTranslation } from '@disco/i18n/use-translation'
import { clientLibraryListGetSingle } from '@disco/state-management/actions/client-library-list-actions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getClientLibrary } from '@disco/state-management/selectors/client-libraries'
import {
  getConstanceSettings,
  isClientLibrariesTrial,
} from '@disco/state-management/selectors/user'
import { useComposerInsteadOfArtist } from '@disco/utils/composerInsteadOfArtist'

import type { ClientLibrary, ClientLibraryId } from 'types/client-library'
import type { State } from 'types/redux'

import { LibraryEasyAccessAlert } from 'components/libraries/libraries-easy-access-alert'
import LibrariesUpgradeAlert from 'components/libraries/libraries-upgrade-alert'
import LibraryPublishActions from 'components/libraries/library-publish-actions'
import Alert from 'components/ui/alert'
import Flex from 'components/ui/flex'
import Lozenge from 'components/ui/lozenge'
import PageHeader from 'components/ui/page-header'
import PageLayout from 'components/ui/page-layout'

import { useLiveLibrariesEnabled } from 'hooks/use-live-libraries-enabled'
import { useDispatch, useSelector } from 'hooks/use-redux'

const LibrariesLibrary = (): React.ReactNode | React.ReactElement<'div'> => {
  const params = useParams<{ libraryId: string }>()
  const location = useLocation()
  const libraryId = params.libraryId as unknown as ClientLibraryId
  const dispatch = useDispatch()
  const { t } = useTranslation('common')
  const library: ClientLibrary | null | undefined = useSelector(
    (state: State) => getClientLibrary(state, libraryId),
  )
  const easyAccess = library?.discovery_access_level === 'discovery_easy_access'
  const clientLibrariesTrial: boolean = useSelector(isClientLibrariesTrial)
  useEffect(() => {
    dispatch(clientLibraryListGetSingle(libraryId))
  }, [dispatch, libraryId])
  const albumsEnabled: boolean = useFeatureEnabled(ALBUMS)
  const licenseRequestEnabled: boolean = useFeatureEnabled(LICENSE_REQUEST)
  const liveLibrariesEnabled = useLiveLibrariesEnabled()

  const constance = useSelector(getConstanceSettings)
  const licenseRequestListingEnabled = constance.LICENSE_REQUESTS_LISTING

  const handleQRAction = useCallback((action: 'download' | 'copy') => {
    mixpanel.sendPureEvent('Catalog QR code interaction', {
      Action: action,
    })
  }, [])

  const typeData = `${useComposerInsteadOfArtist().title}s`
  const NAV_ITEMS = useMemo(
    () => [
      {
        label: 'Content',
        expandable: true,
        items: [
          {
            label: 'Connect channels',
            link: `/catalogs/${libraryId}/content`,
          },
          {
            label: 'Manage tracks',
            link: `/catalogs/${libraryId}/tracks`,
          },
          {
            label: 'Manage playlists',
            link: `/catalogs/${libraryId}/playlists`,
          },
          albumsEnabled && {
            label: 'Manage albums',
            link: `/catalogs/${libraryId}/albums`,
          },
          {
            label: `Manage ${typeData.toLowerCase()}`,
            link: `/catalogs/${libraryId}/artists`,
          },
        ].filter(Boolean),
      },
      {
        label: 'Design',
        expandable: true,
        items: [
          {
            label: 'Catalog design',
            link: `/catalogs/${libraryId}/design`,
          },
          {
            label: 'Contact page',
            link: `/catalogs/${libraryId}/contact`,
          },
        ],
      },
      {
        label: 'Clients & Promotion',
        expandable: true,
        items: [
          {
            label: 'Access & promotion',
            link: `/catalogs/${libraryId}/access`,
          },
          licenseRequestListingEnabled &&
            licenseRequestEnabled && {
              label: 'License requests',
              link: `/catalogs/${libraryId}/license_requests`,
            },
          {
            label: 'Clients',
            link: `/catalogs/${libraryId}/clients`,
          },
          {
            label: 'Stats',
            link: `/catalogs/${libraryId}/stats`,
          },
        ].filter(Boolean),
      },
      {
        label: 'Settings & Notifications',
        expandable: true,
        items: [
          {
            label: 'General settings',
            link: `/catalogs/${libraryId}/settings`,
          },
          {
            label: 'Notifications',
            link: `/catalogs/${libraryId}/notifications`,
          },
        ],
      },
    ],
    [albumsEnabled, licenseRequestEnabled, libraryId, typeData],
  )
  const showEasyAccess = easyAccess && location.pathname.includes('/clients')
  // Show only easy access alert on clients page
  const hideAlerts = showEasyAccess || Boolean(library?.loading)
  const libraryAlert =
    easyAccess && location.pathname.includes('/stats') ? (
      <LibraryEasyAccessAlert />
    ) : null
  const title = (
    <Flex>
      <span translate="no">{library?.name}</span>
      {Boolean(library?.primary) && <Lozenge text="Primary" color="blue" />}
    </Flex>
  )
  return location.state?.modal ? (
    <Outlet />
  ) : (
    <div className="Page">
      <div className="Page_Content">
        <PageLayout title="Catalog settings" nav={NAV_ITEMS}>
          <PageHeader
            breadcrumbs={{
              content: 'Catalogs',
              link: '/catalogs',
            }}
            title={library && title}
            actions={
              <LibraryPublishActions
                libraryId={libraryId}
                library={library}
                publishEnabled={liveLibrariesEnabled}
                viewCaption={t('common:viewCatalog', 'View Catalog')}
                qrFilename={`${library?.name || 'catalog'}-qr`}
                onQRAction={handleQRAction}
              />
            }
          >
            {liveLibrariesEnabled
              ? !hideAlerts && libraryAlert
              : !hideAlerts && (
                  <LibrariesUpgradeAlert
                    clientLibrariesTrial={clientLibrariesTrial}
                  />
                )}

            {showEasyAccess && (
              <div className={styles.libraryAlertContainer}>
                <Alert icon="info" status="info">
                  Your library is configured to be visible to over 800 vetted
                  professional music supervisors. Once they engage in your
                  library by streaming or downloading tracks, they will appear
                  here as clients.{' '}
                  <a
                    className="text-link"
                    href="https://support.disco.ac/home/catalogs-guide#enabling-easy-access-to-make-your-catalog-discoverable-by-music-supervisors-on-disco"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Find out more.
                  </a>
                </Alert>
              </div>
            )}
          </PageHeader>
          <Outlet />
        </PageLayout>
      </div>
    </div>
  )
}

export default LibrariesLibrary
